import { cn } from "@ui/lib/utils";
import type { CSSProperties, ReactNode } from "react";

type FadeColor = "cream" | "white" | "paper";

const fadeColorMap: Record<FadeColor, string> = {
  cream: "#F5F3F1",
  white: "#FFFFFF",
  paper: "#FDFCFC",
};

interface TransparentFadeFrameProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  fadeClassName?: string;
  /** The color used for the top fade gradient. Defaults to `"cream"`. */
  fadeColor?: FadeColor;
}

/**
 * A lightweight chat frame that renders content over a transparent background
 * with a top fade overlay. Unlike `MobileChatFrame`, this has no phone shell
 * or outer wrapper — it's designed for use inside a bento card or similar
 * container where the background is provided externally.
 *
 * Children are centered horizontally and flex-filled vertically so that
 * `ChatScenario` with `anchor="bottom"` works out of the box.
 */
export function TransparentFadeFrame({
  children,
  className,
  contentClassName,
  fadeClassName,
  fadeColor = "cream",
}: TransparentFadeFrameProps) {
  return (
    <div className={cn("tw-relative tw-flex tw-size-full tw-flex-col tw-overflow-hidden", className)}>
      {/* Top fade overlay */}
      <div
        aria-hidden='true'
        className={cn("tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-h-1/3", fadeClassName)}
        style={
          {
            background: `linear-gradient(180deg, ${fadeColorMap[fadeColor]} 0%, transparent 100%)`,
          } satisfies CSSProperties
        }
      />

      {/* Content area — centered horizontally, flex-filled vertically */}
      <div className={cn("tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-center", contentClassName)}>
        <div className='tw-flex tw-min-h-0 tw-w-full tw-max-w-[22.5rem] tw-flex-1 tw-flex-col tw-px-4'>{children}</div>
      </div>
    </div>
  );
}
